<template>
  <v-container
    id="application-content"
    fluid
    tag="main"
    :style="shellStyle"
    class="pb-0"
  >
    <div
      v-if="isLoading"
      id="global-application-loader-overlay"
    >
      <div class="text-center">
        <i class="fa fa-spinner fa-spin fa-2x" />
      </div>
    </div>
    <ada-container v-else>
      <div>
        <demo-dialog
          :visible="showDemoPrompt"
          @acknowledged="handleDemoAcknowledgement"
        />
        <a
          class="skip-to-content"
          href="#application-main"
        >
          {{ $t('skip-to-main-content') }}
        </a>
        <header-component
          id="svp-head"
          :is-expired="isExpired"
          :is-redeemed="isRedeemed"
          :is-locked="isLocked"
          :existing-reward-link-exists="true"
          :logo-source="customizedCompanyLogoUrl"
        />

        <expired-view
          v-if="showExpiredView"
          :reward-link="rewardLink"
        />

        <redeemed-view
          v-else-if="showRedeemedView"
          :is-promo-link="isExpirable"
        />

        <locked-view
          v-else-if="showLockedView"
        />

        <nav-banner
          v-if="!showExpiredView && !showRedeemedView && !showLockedView"
        />

        <article
          id="application-main"
          class="no-error-application-main"
        >
          <slot />
        </article>

        <disclaimers />

        <footer-component
          id="footer"
          :is-expired="isExpired"
          class="text-center"
          :existing-reward-link-exists="true"
        />

        <metrics-container
          v-if="useDatadogRumMetrics && datadogConfig"
          :configuration="datadogConfig"
        />
      </div>
    </ada-container>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import {
  GET_REWARD_LINK,
  GET_CART_TOTAL,
  REWARD_LINK_HAS_EXPIRATION_DATE,
  GET_REWARD_ELEMENT_CUSTOMIZATION_BY_SELECTOR,
  GET_IS_REDEEMED,
  GET_IS_EXPIRED,
  GET_IS_LOCKED
} from '@/store/get-types'
import {
  GET_REWARD_LINK_ASYNC,
  SET_CATALOG,
  UPDATE_REWARD_LINK_BALANCE,
  FETCH_SPECIFIC_RL_RECIPIENT_DATA
} from '@/store/action-types'
import Footer from '../footer/Footer'
import Header from '../Header'
import NavBanner from '../NavBanner'
import ExpiredView from '../ExpiredView'
import RedeemedView from '../RedeemedView'
import Disclaimers from '../Disclaimers'
import LockedView from '@/components/LockedView'
import AdaContainer from '@/components/shared/ada/ada-container'
import { ERROR_MISSING_REQUEST_CODE } from '@/plugins/segment/SegmentConstants'
import { ROUTE_IS_ACTIVE_FOR_EXPIRED_AND_REDEEMED_REWARDS } from '@/router/routes/existing-rl-routes'
import { RewardLinkStatus } from '@/cowboy/EnumTypes'
import { injectAdaChatbotScript } from '@/components/shared/ada/AdaChatbotPlugin'
import { SVP_ADA_CHATBOT_ENABLED, RLSF_DATADOG_RUM_METRICS } from '@/plugins/split/ActiveSplits'
import { MetricsContainer } from '@tangocard/mango'
import { getDatadogRumConfiguration } from '@/configuration'
import { HttpClient } from '@/cowboy/http/HttpClient'
import { StaticRewardLinkService } from '@/service/order_info/StaticRewardLinkService'
import DemoDialog from '@/components/welcome/DemoDialog'

const SVP_BODY_SELECTOR = '#svp-body'
const SVP_HEADER_COMPANY_LOGO_SELECTOR = '#svp-head img.company-logo'

/**
 * @property {StaticRewardLinkService} StaticRewardLinkService
 */
export default {
  name: 'ExistingRewardLinkShell',
  components: {
    DemoDialog,
    Disclaimers,
    'header-component': Header,
    'footer-component': Footer,
    NavBanner,
    ExpiredView,
    RedeemedView,
    LockedView,
    AdaContainer,
    MetricsContainer
  },
  inject: [StaticRewardLinkService.provider],
  data: () => ({
    isLoading: true,
    showDemoPrompt: false
  }),
  computed: {
    ...mapGetters({
      rewardLink: GET_REWARD_LINK,
      cartTotal: GET_CART_TOTAL,
      rewardLinkHasExpirationDate: REWARD_LINK_HAS_EXPIRATION_DATE,
      getElementCustomizationBySelector: GET_REWARD_ELEMENT_CUSTOMIZATION_BY_SELECTOR,
      isRedeemed: GET_IS_REDEEMED,
      isExpired: GET_IS_EXPIRED,
      isLocked: GET_IS_LOCKED
    }),
    catalogId () {
      return this.rewardLink.metadata.catalogId
    },
    isExpirable () {
      return this.rewardLinkHasExpirationDate && !this.isExpired
    },
    shellStyle () {
      const { css: shellStyle } =
        this.getElementCustomizationBySelector(SVP_BODY_SELECTOR) || {}
      return shellStyle
    },
    customizedCompanyLogoUrl () {
      const { attributes = {} } =
        this.getElementCustomizationBySelector(
          SVP_HEADER_COMPANY_LOGO_SELECTOR
        ) || {}
      return attributes.src
    },
    showRedeemedView () {
      const { meta = {} } = this.$route
      return (
        this.isRedeemed &&
        !meta[ROUTE_IS_ACTIVE_FOR_EXPIRED_AND_REDEEMED_REWARDS]
      )
    },
    showExpiredView () {
      const { meta = {} } = this.$route
      return (
        this.isExpired &&
        !meta[ROUTE_IS_ACTIVE_FOR_EXPIRED_AND_REDEEMED_REWARDS]
      )
    },
    showLockedView () {
      const { meta = {} } = this.$route
      return (
        this.isLocked && !meta[ROUTE_IS_ACTIVE_FOR_EXPIRED_AND_REDEEMED_REWARDS]
      )
    },
    datadogConfig () {
      return this.useDatadogRumMetrics ? getDatadogRumConfiguration() : null
    },
    useDatadogRumMetrics () {
      return this.$isSplitEnabled(RLSF_DATADOG_RUM_METRICS)
    }
  },
  beforeMount () {
    this.isLoading = true
    const html = document.documentElement // returns the html tag
    html.setAttribute('lang', this.$i18n.locale)
  },
  async mounted () {
    try {
      // console.log(this.StaticRewardLinkService)
      const orderInfo = this.StaticRewardLinkService.orderInfo
      await this.$split.init(orderInfo.platformHash, orderInfo.utid).catch(error => console.log(error))
      await this.getRewardLinkAsync()
    } catch (e) {
      // if error occurs getting reward link,
      // don't make additional api requests below
      // since error page is shown
      this.displayInvalidRewardLinkError(e)
      return
    }

    if (this.$isSplitEnabled(SVP_ADA_CHATBOT_ENABLED)) {
      try {
        await injectAdaChatbotScript()
      } catch (e) {
        console.error('https://static.ada.support unreachable.', e)
        this.$adaChatbot.setScriptFailure(true)
      }
    }
    // If the the reward is active or expired, proceed since the expiration page depends on mounted being completed. Else throw our general error page
    if (
      this.rewardLink.status !== RewardLinkStatus.ACTIVE &&
      this.rewardLink.status !== RewardLinkStatus.EXPIRED &&
      this.rewardLink.status !== RewardLinkStatus.LOCKED
    ) {
      this.displayInvalidRewardLinkError()
    }
    await this.getAndSetTheCatalog()
    await this.fetchRecipientInfoForSpecificRewardLink()
    this.isLoading = false

    if (!localStorage.getItem('demo-acknowledged')) {
      this.showDemoPrompt = true
    }
  },
  methods: {
    ...mapActions({
      setCatalog: SET_CATALOG,
      updateBalance: UPDATE_REWARD_LINK_BALANCE,
      getRewardLinkAsync: GET_REWARD_LINK_ASYNC,
      fetchRecipientInfoForSpecificRewardLink: FETCH_SPECIFIC_RL_RECIPIENT_DATA
    }),
    displayInvalidRewardLinkError (error) {
      if (error) {
        this.$segment.track(ERROR_MISSING_REQUEST_CODE)
      }
      HttpClient.handleHttpError(
        {
          errorTitle: this.$t('error-invalid-link-title'),
          errorBody: this.$t('error-invalid-link-body')
        },
        error,
        true
      )
    },
    async getAndSetTheCatalog () {
      try {
        await this.setCatalog()
        if (this.cartTotal) {
          this.updateBalance(-this.cartTotal)
        }
      } catch (error) {
        HttpClient.handleHttpError(
          {
            errorTitle: this.$t('error-general-title'),
            errorBody: this.$t('error-catalog-body'),
            details: `${this.$t('error-catalog-details')}${this.catalogId}`
          },
          error
        )
      }
    },
    handleDemoAcknowledgement () {
      this.showDemoPrompt = false
      localStorage.setItem('demo-acknowledged', true)
    }
  }
}
</script>

<style lang="scss">
@import '../../assets/css/global';

#global-application-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 2;

  div {
    position: relative;
    color: black;
    top: 50%;
    margin-top: -15px;
  }
}

.visible-xs {
  display: none;
}

.hidden-xs {
  display: block;
}

@media screen and (max-width: $breakpoint-sm - 1) {
  .visible-xs {
    display: block;
  }

  .hidden-xs {
    display: none;
  }
}

.skip-to-content {
  padding: 1em;
  position: absolute;
  top: -40px;
  left: 1em;
  z-index: 1000;
  color: white;

  &:focus {
    position: absolute;
    top: 0;
    background: #EEEEEE;
  }
}
</style>
